import {
	ElectricalId,
	CablePurpose,
	cableItemById,
	getCatalogItem,
	isLight,
	isJunctionBox,
	isLowVoltagePanel,
	isPanel,
	isSwitch,
} from "@/shared/catalog";
import { leftNormal, norm, pointInPolygon, sub } from "@/shared/lib/geometry";
import {
	allFixtures,
	CatalogSectionKind,
	openingsOfWall,
	wiredEndIds,
	type Fixture,
	type LightingConnection,
	type PlanLayers,
	type WallGraph,
} from "@/shared/plan";
import {
	cableEndPoint,
	cableRoute,
	detectRooms,
	fixturePlanPoint,
	roomFaceSpans,
	roomOfFace,
	roomTitle,
} from "@/widgets/plan";
import { switchesOfLight } from "./logic";
import {
	planAutoWiring,
	chooseAutoCableRoute,
	autoWiringExistingCables,
	suggestLowVoltageSource,
	unusedWiringBoxes,
	type AutoBox,
	type AutoSource,
	type AutoTarget,
	type AutoTargetKind,
} from "./autoWiringPlanner";

export interface AutoLightingLink {
	from: string;
	to: string;
	style: LightingConnection["style"];
	roomName: string;
	switchName: string;
	lightName: string;
	switches: { id: string; name: string }[];
}

export const autoWiringWalls = (graph: WallGraph) =>
	Object.values(graph.walls).flatMap((wall) => {
		const start = graph.nodes[wall.a];
		const end = graph.nodes[wall.b];
		if (!start || !end) return [];
		const length = Math.hypot(end.x - start.x, end.y - start.y);
		const doors = length
			? openingsOfWall(graph, wall.id)
					.filter((opening) => opening.kind === "door")
					.map((opening) => ({
						from: Math.max(0, (opening.offset - opening.width / 2) / length),
						to: Math.min(1, (opening.offset + opening.width / 2) / length),
					}))
			: [];
		return [{ start, end, doors }];
	});

const targetKind = (fixture: Fixture): AutoTargetKind | null => {
	if (isLight(getCatalogItem(fixture.ref)) || isSwitch(getCatalogItem(fixture.ref)))
		return "lighting";
	if (fixture.ref.catalog !== CatalogSectionKind.Electrical) return null;
	switch (fixture.ref.item) {
		case ElectricalId.Socket:
		case ElectricalId.SocketDouble:
		case ElectricalId.SocketTriple:
		case ElectricalId.SocketQuadruple:
		case ElectricalId.SocketQuintuple:
		case ElectricalId.SocketUsb:
			return "outlets";
		case ElectricalId.SocketPower:
		case ElectricalId.SocketAc:
			return "dedicated";
		case ElectricalId.SocketThreePhase:
			return "threePhase";
		case ElectricalId.SocketNet:
			return "network";
		case ElectricalId.SocketTv:
			return "tv";
		default:
			return null;
	}
};

export const buildAutoWiring = (
	layers: PlanLayers,
	style: LightingConnection["style"],
	selectedSources: Record<string, string> = {},
	excludedTargets: ReadonlySet<string> = new Set(),
	rebuild = false,
) => {
	const graph = layers.walls;
	const electrical = layers.electrical;
	const rooms = detectRooms(graph);
	const existingCables = autoWiringExistingCables(layers.wiring.items, rebuild);
	const wired = wiredEndIds(existingCables);
	const fixtures = Object.values(electrical.fixtures);
	const sources: AutoSource[] = fixtures.flatMap((fixture) => {
		const item = getCatalogItem(fixture.ref);
		const point = fixturePlanPoint(graph, fixture);
		if (!point || (!isPanel(item) && !isLowVoltagePanel(item))) return [];
		return [
			{
				id: fixture.id,
				point,
				kind: isPanel(item) ? "power" : "lowVoltage",
				phases: fixture.supplyPhases,
			},
		];
	});
	const warnings: string[] = [];
	const targets: AutoTarget[] = [];
	let skippedWired = 0;
	let skippedRoom = 0;
	for (const fixture of fixtures) {
		const kind = targetKind(fixture);
		if (!kind) continue;
		if (wired.has(fixture.id)) {
			skippedWired++;
			continue;
		}
		const room = roomOfFace(graph, rooms, fixture);
		const point = fixturePlanPoint(graph, fixture);
		if (!room || !point) {
			skippedRoom++;
			continue;
		}
		targets.push({
			id: fixture.id,
			point,
			kind,
			roomId: room.id,
			roomName: roomTitle(layers.rooms, room.id, rooms.indexOf(room)),
		});
	}
	for (const light of Object.values(electrical.items)) {
		if (!isLight(getCatalogItem(light.ref))) continue;
		if (wired.has(light.id)) {
			skippedWired++;
			continue;
		}
		const room = rooms.find((candidate) => pointInPolygon(light, candidate.inner));
		if (!room) {
			skippedRoom++;
			continue;
		}
		targets.push({
			id: light.id,
			point: light,
			kind: "lighting",
			roomId: room.id,
			roomName: roomTitle(layers.rooms, room.id, rooms.indexOf(room)),
		});
	}
	if (skippedWired)
		warnings.push(
			`Приборов с существующими кабелями пропущено: ${skippedWired}. Кабели сохранены; наличие связи с источником нужно проверить отдельно.`,
		);
	if (skippedRoom) warnings.push(`Приборов вне определённых помещений пропущено: ${skippedRoom}.`);
	const boxes: Record<string, AutoBox[]> = {};
	const ceilingBoxes: Record<string, AutoBox[]> = {};
	const plannedTargetIds = new Set(targets.map((target) => target.id));
	const reusableBoxes = new Set(
		Object.values(layers.wiring.items).flatMap((cable) => [
			...(plannedTargetIds.has(cable.to) ? [cable.from] : []),
			...(plannedTargetIds.has(cable.from) ? [cable.to] : []),
		]),
	);
	const obsoleteBoxes = new Set(
		rebuild
			? unusedWiringBoxes(
					{ ...layers, wiring: { ...layers.wiring, items: existingCables } },
					false,
				).ceilingItems.filter((id) => !reusableBoxes.has(id))
			: [],
	);
	const placedFixtures = allFixtures(layers);
	for (const room of rooms) {
		const candidates: AutoBox[] = [];
		const ceilingCandidates: AutoBox[] = [];
		for (const span of roomFaceSpans(graph, room)) {
			const wall = graph.walls[span.wallId];
			if (!wall || wall.height < 500) continue;
			for (
				let offset = Math.ceil((span.from + 300) / 300) * 300;
				offset <= span.to - 300;
				offset += 300
			) {
				if (
					openingsOfWall(graph, wall.id).some(
						(opening) => Math.abs(opening.offset - offset) < opening.width / 2 + 150,
					)
				)
					continue;
				if (
					placedFixtures.some(
						(fixture) => fixture.wallId === wall.id && Math.abs(fixture.offset - offset) < 200,
					)
				)
					continue;
				const elevation = wall.height - 200;
				const point = fixturePlanPoint(graph, {
					id: "draft",
					wallId: wall.id,
					ref: { catalog: CatalogSectionKind.Electrical, item: ElectricalId.JunctionBox },
					offset,
					elevation,
					side: span.side,
				});
				if (point) {
					candidates.push({ wallId: wall.id, side: span.side, offset, elevation, point });
					const start = graph.nodes[wall.a];
					const end = graph.nodes[wall.b];
					if (!start || !end) continue;
					const normal = leftNormal(norm(sub(end, start)));
					const ceilingPoint = {
						x: Math.round(point.x + normal.x * span.side * 250),
						y: Math.round(point.y + normal.y * span.side * 250),
					};
					if (
						pointInPolygon(ceilingPoint, room.inner) &&
						Object.values(electrical.items).every(
							(item) =>
								obsoleteBoxes.has(item.id) ||
								Math.hypot(item.x - ceilingPoint.x, item.y - ceilingPoint.y) >= 200,
						)
					)
						ceilingCandidates.push({
							wallId: wall.id,
							side: span.side,
							offset,
							elevation,
							point: ceilingPoint,
							ceiling: true,
						});
				}
			}
		}
		if (rebuild) {
			for (const item of Object.values(electrical.items)) {
				if (!isJunctionBox(getCatalogItem(item.ref)) || !pointInPolygon(item, room.inner)) continue;
				const targetIds = Object.values(layers.wiring.items).flatMap((cable) =>
					cable.from === item.id ? [cable.to] : cable.to === item.id ? [cable.from] : [],
				);
				if (targetIds.length)
					ceilingCandidates.push({
						wallId: "",
						side: 1,
						offset: 0,
						elevation: 0,
						point: { x: item.x, y: item.y },
						ceiling: true,
						existingId: item.id,
						targetIds,
					});
			}
		}
		boxes[room.id] = candidates;
		ceilingBoxes[room.id] = ceilingCandidates;
	}
	const suggestedSource = suggestLowVoltageSource(targets, sources, Object.values(boxes).flat());
	if (suggestedSource) {
		const item = getCatalogItem({
			catalog: CatalogSectionKind.Electrical,
			item: ElectricalId.LowVoltagePanel,
		});
		const wall = graph.walls[suggestedSource.placement!.wallId];
		suggestedSource.placement!.elevation = Math.min(item?.elevation ?? 0, wall.height);
		sources.push(suggestedSource);
	}
	const wiring = planAutoWiring(targets, sources, ceilingBoxes, selectedSources, excludedTargets);
	const walls = autoWiringWalls(graph);
	const routes = Object.values(existingCables).flatMap((cable) => {
		const material = cable.material ? cableItemById(cable.material) : null;
		const route = cableRoute(graph, electrical, cable);
		return material && route
			? [
					{
						route,
						low:
							material.purpose === CablePurpose.LowVoltage || material.purpose === CablePurpose.Tv,
					},
				]
			: [];
	});
	for (const group of wiring.groups) {
		const low = group.purpose === "network" || group.purpose === "tv";
		group.cables = group.cables.map((cable) => {
			const chosen = chooseAutoCableRoute(
				cable,
				walls,
				routes.filter((other) => other.low !== low).map((other) => other.route),
				routes.map((other) => other.route),
			);
			routes.push({ route: chosen.route, low });
			return chosen;
		});
	}
	const links: AutoLightingLink[] = [];
	const switches = fixtures.filter((fixture) => isSwitch(getCatalogItem(fixture.ref)));
	const lights = [
		...fixtures.filter((fixture) => isLight(getCatalogItem(fixture.ref))),
		...Object.values(electrical.items).filter((item) => isLight(getCatalogItem(item.ref))),
	];
	for (const light of lights) {
		if (
			switchesOfLight(electrical, layers.wiring.items, light.id, layers.wiring.connections)
				.length ||
			Object.values(layers.wiring.connections).some(
				(link) => link.from === light.id || link.to === light.id,
			)
		)
			continue;
		const point = cableEndPoint(graph, electrical, light.id);
		const room =
			"wallId" in light
				? roomOfFace(graph, rooms, light)
				: rooms.find((candidate) => pointInPolygon(light, candidate.inner));
		if (!point || !room) continue;
		const candidates = switches
			.filter((fixture) => roomOfFace(graph, rooms, fixture)?.id === room.id)
			.map((fixture) => ({ fixture, point: fixturePlanPoint(graph, fixture) }))
			.filter((candidate) => candidate.point !== null)
			.toSorted(
				(left, right) =>
					Math.hypot(left.point!.x - point.x, left.point!.y - point.y) -
					Math.hypot(right.point!.x - point.x, right.point!.y - point.y),
			);
		const nearest = candidates[0];
		if (!nearest) continue;
		links.push({
			from: nearest.fixture.id,
			to: light.id,
			style,
			roomName: roomTitle(layers.rooms, room.id, rooms.indexOf(room)),
			switchName: getCatalogItem(nearest.fixture.ref)?.name ?? "Выключатель",
			lightName: getCatalogItem(light.ref)?.name ?? "Светильник",
			switches: candidates.map(({ fixture }) => ({
				id: fixture.id,
				name: getCatalogItem(fixture.ref)?.name ?? "Выключатель",
			})),
		});
	}
	return { ...wiring, warnings: [...warnings, ...wiring.warnings], links, sources };
};
